import { useState, useRef, useEffect, useCallback, useId } from 'react';
import type { UserInfo } from './types';
import { Icon } from './Icon';

interface UserDropdownProps {
  user: UserInfo;
  onLogout: () => void;
  onProfileClick: () => void;
  onPasswordChange: () => void;
  onActivityHistory?: () => void;
  userMenuAriaLabel?: (displayName: string) => string;
  profileLabel?: string;
  passwordChangeLabel?: string;
  activityHistoryLabel?: string;
  logoutLabel?: string;
  roleLabels?: Partial<Record<'ROLE_GUEST' | 'ROLE_ADMIN' | 'ROLE_MANAGER' | 'ROLE_USER', string>>;
}

const DEFAULT_ROLE_LABEL: Record<string, string> = {
  ROLE_GUEST: '게스트',
  ROLE_ADMIN: '관리자',
  ROLE_MANAGER: '매니저',
  ROLE_USER: '사용자',
};

export function UserDropdown({
  user,
  onLogout,
  onProfileClick,
  onPasswordChange,
  onActivityHistory,
  userMenuAriaLabel,
  profileLabel,
  passwordChangeLabel,
  activityHistoryLabel,
  logoutLabel,
  roleLabels,
}: UserDropdownProps) {
  const resolvedRoleLabel = { ...DEFAULT_ROLE_LABEL, ...roleLabels };
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const menuId = useId();

  const close = useCallback(() => setOpen(false), []);

  // 외부 클릭 닫기 (R7-014: pointerdown 으로 변경해 모바일 포인터 이벤트도 포착)
  useEffect(() => {
    if (!open) return;
    const handler = (e: PointerEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        close();
      }
    };
    document.addEventListener('pointerdown', handler);
    return () => document.removeEventListener('pointerdown', handler);
  }, [open, close]);

  // Escape 닫기 (R7-095)
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        close();
        triggerRef.current?.focus();
      }
    };
    document.addEventListener('keydown', handler, { capture: true });
    return () => document.removeEventListener('keydown', handler, { capture: true });
  }, [open, close]);

  // 메뉴 열릴 때 첫 번째 menuitem 으로 포커스 이동
  useEffect(() => {
    if (open) {
      const id = requestAnimationFrame(() => {
        const first = menuRef.current?.querySelector<HTMLElement>('[role="menuitem"]');
        first?.focus();
      });
      return () => cancelAnimationFrame(id);
    }
  }, [open]);

  const initials = user.displayName
    .split(' ')
    .map((w) => w[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

  const menuItems = [
    { label: profileLabel ?? '내 프로필', action: onProfileClick, icon: 'user' },
    { label: passwordChangeLabel ?? '비밀번호 변경', action: onPasswordChange, icon: 'lock' },
    ...(onActivityHistory
      ? [{ label: activityHistoryLabel ?? '내 활동 기록', action: onActivityHistory, icon: 'server' }]
      : []),
    { label: logoutLabel ?? '로그아웃', action: onLogout, icon: 'logout', danger: true },
  ];

  return (
    <div ref={containerRef} style={{ position: 'relative' }}>
      <button
        ref={triggerRef}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        aria-label={userMenuAriaLabel ? userMenuAriaLabel(user.displayName) : `사용자 메뉴: ${user.displayName}`}
        onClick={() => setOpen((v) => !v)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--spacing-2)',
          background: 'none',
          border: '1px solid var(--color-surface-border)',
          borderRadius: 'var(--radius-full)',
          padding: 'var(--spacing-1) var(--spacing-2)',
          cursor: 'pointer',
          color: 'var(--color-text-primary)',
          fontSize: 'var(--font-size-sm)',
          transition: 'background 0.15s',
        }}
        onMouseEnter={(e) => {
          (e.currentTarget as HTMLButtonElement).style.background =
            'var(--color-surface-muted)';
        }}
        onMouseLeave={(e) => {
          (e.currentTarget as HTMLButtonElement).style.background = 'none';
        }}
      >
        {/* 아바타 원 */}
        <span
          aria-hidden="true"
          style={{
            width: '28px',
            height: '28px',
            borderRadius: 'var(--radius-full)',
            background: 'var(--color-brand-primary)',
            color: 'var(--color-text-inverse)',
            fontSize: 'var(--font-size-xs)',
            fontWeight: 700,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          {initials}
        </span>
        <span style={{ maxWidth: '120px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {user.displayName}
        </span>
        <Icon id="chevron-down" size={14} />
      </button>

      {open && (
        <div
          ref={menuRef}
          id={menuId}
          role="menu"
          aria-label="사용자 메뉴"
          style={{
            position: 'absolute',
            top: 'calc(100% + var(--spacing-2))',
            right: 0,
            minWidth: '180px',
            background: 'var(--color-surface-background)',
            border: '1px solid var(--color-surface-border)',
            borderRadius: 'var(--radius-md)',
            boxShadow: 'var(--shadow-md)',
            zIndex: 200,
            overflow: 'hidden',
          }}
        >
          {/* 사용자 요약 */}
          <div
            style={{
              padding: 'var(--spacing-3) var(--spacing-4)',
              borderBottom: '1px solid var(--color-surface-border)',
            }}
          >
            <div
              style={{
                fontSize: 'var(--font-size-sm)',
                fontWeight: 600,
                color: 'var(--color-text-primary)',
              }}
            >
              {user.displayName}
            </div>
            <div
              style={{
                fontSize: 'var(--font-size-xs)',
                color: 'var(--color-text-muted)',
                marginTop: '2px',
              }}
            >
              {resolvedRoleLabel[user.role] ?? user.role}
            </div>
          </div>

          {/* 메뉴 아이템 */}
          {menuItems.map((item) => (
            <button
              key={item.label}
              type="button"
              role="menuitem"
              onClick={() => {
                close();
                item.action();
              }}
              style={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--spacing-2)',
                padding: 'var(--spacing-3) var(--spacing-4)',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                fontSize: 'var(--font-size-sm)',
                color: item.danger
                  ? 'var(--color-danger)'
                  : 'var(--color-text-primary)',
                textAlign: 'left',
                transition: 'background 0.1s',
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLButtonElement).style.background =
                  'var(--color-surface-muted)';
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLButtonElement).style.background = 'none';
              }}
            >
              <Icon id={item.icon} size={16} />
              {item.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
